/**
 * Página de Cambio de Contraseña
 * Se muestra cuando un usuario con contraseña temporal intenta iniciar sesión
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

function NewPasswordPage() {
  const navigate = useNavigate();
  const { user, pendingPasswordChallenge, confirmNewPassword, authLoading } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Redirigir si ya está autenticado o no hay desafío pendiente
  useEffect(() => {
    if (!authLoading) {
      if (user) {
        navigate('/', { replace: true });
      } else if (!pendingPasswordChallenge) {
        navigate('/login', { replace: true });
      }
    }
  }, [user, pendingPasswordChallenge, authLoading, navigate]);

  // Validar contraseña en tiempo real
  useEffect(() => {
    const password = formData.newPassword;
    setPasswordValidation({
      hasMinLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[^A-Za-z0-9]/.test(password)
    });
  }, [formData.newPassword]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidation).every(v => v === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validaciones
    if (!formData.email || !formData.newPassword || !formData.confirmPassword) {
      setError('Por favor completa todos los campos');
      setLoading(false);
      return;
    }

    if (!isPasswordValid()) {
      setError('La contraseña no cumple con los requisitos de seguridad');
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }

    try {
      const result = await confirmNewPassword(formData.newPassword, formData.email);

      if (result.success) {
        navigate('/', { replace: true });
      } else {
        setError(result.error || 'Error al cambiar la contraseña');
      }
    } catch (err) {
      console.error('Error cambiando contraseña:', err);
      setError('Error al cambiar la contraseña. Por favor intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-container">
          <p>Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>🍽️ Digital Menu</h1>
          <h2>Cambiar Contraseña</h2>
          <p>Debes cambiar tu contraseña temporal antes de continuar</p>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="tu@email.com"
              required
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Nueva Contraseña</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="••••••••"
              required
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmar Contraseña</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="••••••••"
              required
              disabled={loading}
              autoComplete="new-password"
            />
          </div>

          {/* Requisitos de contraseña */}
          <div className="password-requirements">
            <p className="requirements-title">La contraseña debe contener:</p>
            <ul>
              <li className={passwordValidation.hasMinLength ? 'valid' : ''}>
                ✓ Al menos 8 caracteres
              </li>
              <li className={passwordValidation.hasUppercase ? 'valid' : ''}>
                ✓ Al menos una letra mayúscula
              </li>
              <li className={passwordValidation.hasLowercase ? 'valid' : ''}>
                ✓ Al menos una letra minúscula
              </li>
              <li className={passwordValidation.hasNumber ? 'valid' : ''}>
                ✓ Al menos un número
              </li>
              <li className={passwordValidation.hasSpecialChar ? 'valid' : ''}>
                ✓ Al menos un carácter especial (!@#$%^&*)
              </li>
            </ul>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !isPasswordValid()}
          >
            {loading ? 'Cambiando contraseña...' : 'Cambiar Contraseña'}
          </button>
        </form>

        <div className="login-footer">
          <p className="help-text">
            Tu nueva contraseña será utilizada en futuros inicios de sesión.
          </p>
        </div>
      </div>
    </div>
  );
}

export default NewPasswordPage;
